'use client';

import {
  DEFAULT_TENANT_UI_CONFIG,
  isLtsDefaultUiOverrideAccount,
  TenantUiConfig,
} from '@/shared/constants/custom-rendering-accounts';
import { useAccountMeAuthApi } from '@/shared/apis/account/account-me';
import { useGetSpaceMeApi } from '@/shared/apis/space/get-space-me';
import { useGetSpaceUiProfileApi } from '@/shared/apis/space/get-space-ui-profile';
import {
  applyLtsDefaultUiOverride,
  buildTenantUiConfigFromProfile,
} from '@/shared/lib/space-ui-profile';
import { useMemo } from 'react';

/**
 * 현재 사용자의 UI 설정을 반환하는 훅.
 *
 * space UI 프로파일 serve(GET /space/ui-profile)가 유일 소스 — base(DEFAULT) 위에
 * serve delta 를 재구성한 TenantUiConfig 를 반환한다. 구 TENANT_UI_CONFIGS(tenant 상수)
 * 폴백은 제거됐다(space-ui-fixes): 미시드 space / 로딩·에러 = base(DEFAULT) 동작.
 * account 14744 등 LTS DEFAULT 강제 오버라이드는 유지(§1.6 특례).
 */
export function useTenantUiConfig(): TenantUiConfig {
  return useTenantUiConfigWithStatus().config;
}

/**
 * UI 설정 + 로딩 상태.
 *
 * 프로파일 도착 전에는 base(DEFAULT)를 쓰기 때문에, 그대로 그리면 설정이 off 인 요소가
 * 잠깐 보였다 사라진다(코인 잔액 등). 깜빡임을 피해야 하는 표면은 이 훅의 isResolved 가
 * true 가 될 때까지 렌더를 보류한다.
 */
export function useTenantUiConfigWithStatus(): {
  config: TenantUiConfig;
  isResolved: boolean;
} {
  const { data: accountMe } = useAccountMeAuthApi();
  const { data: spaceMe, isError: isSpaceMeError } = useGetSpaceMeApi();
  // ui-profile serve 는 현재 Space(@CurrentSpaceId) 필수 → space 있을 때만 조회.
  const {
    data: uiProfileRes,
    isPending: isUiProfilePending,
    isError: isUiProfileError,
  } = useGetSpaceUiProfileApi({
    enabled: Boolean(spaceMe?.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const accountId = accountMe?.data?.id;
  const uiProfile = uiProfileRes?.data;
  // space 미확정(=조회 자체가 비활성)이면 아직 판정 불가. space 확정 시 ui-profile settle 을
  // 기다리고, space 자체가 에러로 끝나면(무-space·PENDING/REJECTED 등) base 로 확정한다
  // (spaceMe 실패를 무시하면 isResolved 가 영구 false 로 고착돼 대기 화면이 멈춘다).
  const isResolved = spaceMe?.data
    ? !isUiProfilePending || isUiProfileError
    : isSpaceMeError;

  return useMemo(() => {
    const built = uiProfile
      ? buildTenantUiConfigFromProfile(uiProfile, DEFAULT_TENANT_UI_CONFIG)
      : DEFAULT_TENANT_UI_CONFIG;
    const config = isLtsDefaultUiOverrideAccount(accountId)
      ? applyLtsDefaultUiOverride(built)
      : built;
    return { config, isResolved };
  }, [uiProfile, accountId, isResolved]);
}

/**
 * 현재 space 가 GS 전용 모드인지 반환하는 훅.
 * 구 isGsrAccount(tenantId) 하드코딩을 대체 — space 단위 gsExclusive 플래그(신 소스)를 읽는다.
 */
export function useIsGsExclusive(): boolean {
  return useTenantUiConfig().gsExclusive;
}

/** 현재 Space에서 내보내기 FPS 선택을 허용하는지 반환한다. */
export function useIsExportFpsSelectionEnabled(): boolean {
  return useTenantUiConfig().exportFpsSelectionEnabled;
}
